import { get, isEmpty, keys, localStorageManager, map, StorageKeyManager } from '~/utils';
import Intl from '@fx-ui/intl';
import type CorpUserModel from '~/models/corpUser';
import { CONTACTS } from '~/constants';
import { DeptType } from '@fx-ui/platform-sdk';
import { CorpType, MEMBER_INVITE_FLAG } from '~/interfaces';

/**
 * 获取用户画像中的【你的需求】国际化
 */
const getRequirementsIntl = (): Record<string, string> => {
    return {
        selection: Intl.get('personal.requirements.selection'),
        proxy: Intl.get('personal.requirements.proxy'),
        teaching: Intl.get('personal.requirements.teaching'),
        personal: Intl.get('personal.requirements.personal'),
        other: Intl.get('personal.requirements.other'),
        jodoo_work: Intl.get('personal.requirements.jodoo.work'),
        jodoo_personal: Intl.get('personal.requirements.jodoo.personal')
    };
};

/**
 * 获取用户画像中的【行业】国际化
 */
const getIndustriesIntl = (): Record<string, string> => {
    return {
        construction: Intl.get('personal.industries.construction'),
        education: Intl.get('personal.industries.education'),
        electricity: Intl.get('personal.industries.electricity'),
        finance: Intl.get('personal.industries.finance'),
        government: Intl.get('personal.industries.government'),
        internet: Intl.get('personal.industries.internet'),
        leasing: Intl.get('personal.industries.leasing'),
        manufacturing: Intl.get('personal.industries.manufacturing'),
        medical_medicine: Intl.get('personal.industries.medical.medicine'),
        other: Intl.get('personal.industries.other'),
        scientific: Intl.get('personal.industries.scientific'),
        service: Intl.get('personal.industries.service'),
        trade: Intl.get('personal.industries.trade'),
        transportation: Intl.get('personal.industries.transportation'),
        jodoo_construction: Intl.get('personal.industries.jodoo.construction'),
        jodoo_education: Intl.get('personal.industries.jodoo.education'),
        jodoo_energy: Intl.get('personal.industries.jodoo.energy'),
        jodoo_finance: Intl.get('personal.industries.jodoo.finance'),
        jodoo_government: Intl.get('personal.industries.jodoo.government'),
        jodoo_healthcare: Intl.get('personal.industries.jodoo.healthcare'),
        jodoo_hospitality: Intl.get('personal.industries.jodoo.hospitality'),
        jodoo_itInternet: Intl.get('personal.industries.jodoo.itInternet'),
        jodoo_logistic: Intl.get('personal.industries.jodoo.logistic'),
        jodoo_manufacturing: Intl.get('personal.industries.jodoo.manufacturing'),
        jodoo_nonProfit: Intl.get('personal.industries.jodoo.nonProfit'),
        jodoo_others: Intl.get('personal.industries.jodoo.others'),
        jodoo_publicUtility: Intl.get('personal.industries.jodoo.publicUtility'),
        jodoo_realEstate: Intl.get('personal.industries.jodoo.realEstate'),
        jodoo_retailWholesale: Intl.get('personal.industries.jodoo.retailWholesale'),
        analytics_ecommerce_cross_border: Intl.get('fxp.base.workspace.login.ecommerce.opt'),
        analytics_catering_hotel: Intl.get('fxp.base.workspace.login.catering_hotel.opt'),
        analytics_wholesale_retail: Intl.get('fxp.base.workspace.login.wholesale_retail.opt'),
        analytics_internet_software_communication: Intl.get(
            'fxp.base.workspace.login.internet_software_telecom.opt'
        ),
        analytics_medicine_medical_beauty: Intl.get(
            'fxp.base.workspace.login.pharma_medical_aesthetics.opt'
        ),
        analytics_transportation_logistics: Intl.get(
            'fxp.base.workspace.login.transport_logistics.opt'
        ),
        analytics_leasing_business_service: Intl.get(
            'fxp.base.workspace.login.leasing_business_services.opt'
        ),
        analytics_education_training: Intl.get('fxp.base.workspace.login.education_training.opt'),
        analytics_manufacturing: Intl.get('personal.industries.manufacturing'),
        analytics_others: Intl.get('personal.industries.jodoo.others'),
        governance_manufacturing: Intl.get('personal.industries.manufacturing'),
        governance_trade: Intl.get('personal.industries.trade'),
        governance_internet: Intl.get('personal.industries.internet'),
        governance_medical_medicine: Intl.get('personal.industries.medical.medicine'),
        governance_electricity: Intl.get('personal.industries.electricity'),
        governance_finance: Intl.get('personal.industries.finance'),
        governance_construction: Intl.get('personal.industries.construction'),
        governance_education: Intl.get('personal.industries.education'),
        governance_leasing: Intl.get('personal.industries.leasing'),
        governance_scientific: Intl.get('personal.industries.scientific'),
        governance_transportation: Intl.get('personal.industries.transportation'),
        governance_government: Intl.get('personal.industries.government'),
        governance_other: Intl.get('personal.industries.other')
    };
};

/**
 * 获取用户画像中的【管理需求（应用场景）】国际化
 */
export const getFavoritesIntl = (): Record<string, string> => {
    return {
        after_sale: Intl.get('persona.favorites.after.sale'),
        attendance: Intl.get('persona.favorites.attendance'),
        contract: Intl.get('persona.favorites.contract'),
        crm: Intl.get('persona.favorites.crm'),
        device: Intl.get('persona.favorites.device'),
        educate: Intl.get('persona.favorites.educate'),
        erp: Intl.get('persona.favorites.erp'),
        finance: Intl.get('persona.favorites.finance'),
        hr: Intl.get('persona.favorites.hr'),
        hse: Intl.get('persona.favorites.hse'),
        inventory: Intl.get('persona.favorites.inventory'),
        it_project: Intl.get('persona.favorites.it.project'),
        it_sci_crm: Intl.get('persona.favorites.it.sci.crm'),
        item: Intl.get('persona.favorites.item'),
        kpi: Intl.get('persona.favorites.kpi'),
        manufacturing_quality: Intl.get('persona.favorites.manufacturing.quality'),
        manufacturing_sales: Intl.get('persona.favorites.manufacturing.sales'),
        materials: Intl.get('persona.favorites.materials'),
        'multi-project': Intl.get('persona.favorites.multi.project'),
        oa: Intl.get('persona.favorites.oa'),
        produce: Intl.get('persona.favorites.produce'),
        production_research: Intl.get('persona.favorites.production.research'),
        progress: Intl.get('persona.favorites.progress'),
        rear_service: Intl.get('persona.favorites.rear.service'),
        store: Intl.get('persona.favorites.store'),
        student: Intl.get('persona.favorites.student'),
        supplier: Intl.get('persona.favorites.supplier'),
        vehicle: Intl.get('persona.favorites.vehicle'),
        wms: Intl.get('persona.favorites.wms'),
        jodoo_accounting: Intl.get('persona.favorites.jodoo.accounting'),
        jodoo_admin: Intl.get('persona.favorites.jodoo.admin'),
        jodoo_after_sales: Intl.get('persona.favorites.jodoo.after.sales'),
        jodoo_attendance: Intl.get('persona.favorites.jodoo.attendance'),
        jodoo_bug_tracking: Intl.get('persona.favorites.jodoo.bug.tracking'),
        jodoo_business: Intl.get('persona.favorites.jodoo.business'),
        jodoo_construction: Intl.get('persona.favorites.jodoo.construction'),
        jodoo_course: Intl.get('persona.favorites.jodoo.course'),
        jodoo_crm: Intl.get('persona.favorites.jodoo.crm'),
        jodoo_dining: Intl.get('persona.favorites.jodoo.dining'),
        jodoo_dorm: Intl.get('persona.favorites.jodoo.dorm'),
        jodoo_ehs: Intl.get('persona.favorites.jodoo.ehs'),
        jodoo_equipment: Intl.get('persona.favorites.jodoo.equipment'),
        jodoo_erp: Intl.get('persona.favorites.jodoo.erp'),
        jodoo_warehouse: Intl.get('persona.favorites.jodoo.warehouse'),
        jodoo_sales: Intl.get('persona.favorites.jodoo.sales'),
        jodoo_finance: Intl.get('persona.favorites.jodoo.finance'),
        jodoo_franchise: Intl.get('persona.favorites.jodoo.franchise'),
        jodoo_hotel: Intl.get('persona.favorites.jodoo.hotel'),
        jodoo_hr: Intl.get('persona.favorites.jodoo.hr'),
        jodoo_inventory_warehouse: Intl.get('persona.favorites.jodoo.inventory.warehouse'),
        jodoo_it_asset: Intl.get('persona.favorites.jodoo.it.asset'),
        jodoo_itsm: Intl.get('persona.favorites.jodoo.itsm'),
        jodoo_kpi: Intl.get('persona.favorites.jodoo.kpi'),
        jodoo_labor_service: Intl.get('persona.favorites.jodoo.labor.service'),
        jodoo_library: Intl.get('persona.favorites.jodoo.library'),
        jodoo_maintenance: Intl.get('persona.favorites.jodoo.maintenance'),
        jodoo_materials: Intl.get('persona.favorites.jodoo.materials'),
        jodoo_oms: Intl.get('persona.favorites.jodoo.oms'),
        jodoo_operation: Intl.get('persona.favorites.jodoo.operation'),
        jodoo_others: Intl.get('persona.favorites.jodoo.others'),
        jodoo_pre_sales: Intl.get('persona.favorites.jodoo.pre.sales'),
        jodoo_product_development: Intl.get('persona.favorites.jodoo.product.development'),
        jodoo_production: Intl.get('persona.favorites.jodoo.production'),
        jodoo_project: Intl.get('persona.favorites.jodoo.project'),
        jodoo_property_agent_crm: Intl.get('persona.favorites.jodoo.property.agent.crm'),
        jodoo_quality: Intl.get('persona.favorites.jodoo.quality'),
        jodoo_quotes: Intl.get('persona.favorites.jodoo.quotes'),
        jodoo_reservation: Intl.get('persona.favorites.jodoo.reservation'),
        jodoo_restaurant: Intl.get('persona.favorites.jodoo.restaurant'),
        jodoo_schedule: Intl.get('persona.favorites.jodoo.schedule'),
        jodoo_scm: Intl.get('persona.favorites.jodoo.scm'),
        jodoo_store: Intl.get('persona.favorites.jodoo.store'),
        jodoo_vehicle: Intl.get('persona.favorites.jodoo.vehicle'),
        analytics_advertising: Intl.get('fxp.base.workspace.login.advertising.opt'),
        analytics_traffic: Intl.get('fxp.base.workspace.login.traffic.opt'),
        analytics_sales: Intl.get('persona.favorites.manufacturing.sales'),
        analytics_profit: Intl.get('fxp.base.workspace.login.profit.opt'),
        analytics_stock: Intl.get('fxp.base.workspace.login.inventory.opt'),
        analytics_product_selection: Intl.get('fxp.base.workspace.login.prod_select.opt'),
        analytics_finance: Intl.get('persona.favorites.finance'),
        analytics_audience: Intl.get('fxp.base.workspace.login.audience.opt'),
        analytics_others: Intl.get('persona.favorites.jodoo.others'),
        governance_data_warehouse_development: Intl.get('fxp.base.workspace.login.dw_develop.opt'),
        governance_data_management: Intl.get('fxp.base.workspace.login.data_mgmt.opt'),
        governance_data_governance: Intl.get('fxp.base.workspace.login.data_gov.opt'),
        governance_enterprise_data_catalog: Intl.get(
            'fxp.base.workspace.login.unified_data_catalog.opt'
        )
    };
};

/**
 * 获取用户画像中的【角色】国际化
 */
const getJobsIntl = (): Record<string, string> => {
    return {
        boss: Intl.get('personal.job.boss'),
        it: Intl.get('personal.job.it'),
        student: Intl.get('personal.job.student'),
        teacher: Intl.get('personal.job.teacher'),
        jodoo_boss: Intl.get('personal.job.jodoo.boss'),
        'jodoo_it-leader': Intl.get('personal.job.jodoo.it.leader'),
        jodoo_manager: Intl.get('personal.job.jodoo.manager'),
        jodoo_staff: Intl.get('personal.job.jodoo.staff'),
        'jodoo_it-personnel': Intl.get('personal.job.jodoo.it.personnel'),
        jodoo_freelancer: Intl.get('personal.job.jodoo.freelancer'),
        jodoo_teacher: Intl.get('personal.job.jodoo.teacher'),
        jodoo_student: Intl.get('personal.job.jodoo.student'),
        manager: Intl.get('personal.job.manager'),
        staff: Intl.get('personal.job.staff'),
        cio: Intl.get('fxp.base.workspace.login.cio_it_head.opt')
    };
};

/**
 * 获取用户画像中的【了解渠道】国际化
 */
const getReferrersIntl = (): Record<string, string> => {
    return {
        friend: Intl.get('persona.referrers.friend'),
        jodoo_facebook: Intl.get('persona.referrers.jodoo.facebook'),
        jodoo_friends: Intl.get('persona.referrers.jodoo.friends'),
        jodoo_linkedIn: Intl.get('persona.referrers.jodoo.linkedIn'),
        jodoo_others: Intl.get('persona.referrers.jodoo.others'),
        jodoo_twitter: Intl.get('persona.referrers.jodoo.twitter'),
        jodoo_web_search: Intl.get('persona.referrers.jodoo.web.search'),
        jodoo_youtube: Intl.get('persona.referrers.jodoo.youtube'),
        jodoo_ai_recommend: Intl.get('persona.referrers.jodoo.ai.recommend'),
        others: Intl.get('persona.referrers.others'),
        search: Intl.get('persona.referrers.search'),
        shortvedio: Intl.get('persona.referrers.shortvedio'),
        toutiao: Intl.get('persona.referrers.toutiao'),
        wechat: Intl.get('persona.referrers.wechat'),
        xiaohongshu: Intl.get('persona.referrers.xiaohongshu'),
        ai_recommend: Intl.get('persona.referrers.ai.recommend'),
        zhihu: Intl.get('persona.referrers.zhihu'),
        analytics_ai_recommend: Intl.get('persona.referrers.ai.recommend'),
        analytics_baidu_search: Intl.get('fxp.base.workspace.login.baidu_search.opt'),
        analytics_zhihu: Intl.get('persona.referrers.zhihu'),
        analytics_collaboration_platform: Intl.get('fxp.base.workspace.login.im_tools.opt'),
        analytics_xiaohongshu: Intl.get('persona.referrers.xiaohongshu'),
        analytics_wechat: Intl.get('persona.referrers.wechat'),
        analytics_video: Intl.get('fxp.base.workspace.login.video.opt'),
        analytics_fanruan_jodoo: Intl.get('fxp.base.workspace.login.fanruan_jiandaoyun.opt'),
        analytics_friend: Intl.get('persona.referrers.friend'),
        analytics_others: Intl.get('persona.referrers.others'),
        governance_ai_recommend: Intl.get('persona.referrers.ai.recommend'),
        governance_baidu_search: Intl.get('fxp.base.workspace.login.baidu_search.opt'),
        governance_zhihu: Intl.get('persona.referrers.zhihu'),
        governance_collaboration_platform: Intl.get('fxp.base.workspace.login.im_tools.opt'),
        governance_xiaohongshu: Intl.get('persona.referrers.xiaohongshu'),
        governance_wechat: Intl.get('persona.referrers.wechat'),
        governance_friend: Intl.get('persona.referrers.friend'),
        governance_others: Intl.get('persona.referrers.others')
    };
};

/**
 * 获取用户画像中的【你的需求】标签数据
 *
 * @param originLabelData 后端返回的原始标签数据
 */
export const getRequirements = (originLabelData: Record<string, any>) => {
    const requirements = getRequirementsIntl();
    return map(get(originLabelData, 'requirement'), (requirement) => {
        return { value: requirement, text: get(requirements, requirement) };
    });
};

/**
 * 获取用户画像中的【所属行业】标签数据
 *
 * @param originLabelData 后端返回的原始标签数据
 */
export const getCorpIndustry = (originLabelData: Record<string, any>) => {
    const industries = getIndustriesIntl();
    return map(keys(get(originLabelData, 'industries')), (industry) => {
        return { value: industry, text: get(industries, industry) };
    });
};

/**
 * 管理需求是否为【单独配置】场景
 *
 * 后端在 originLabelData 中返回 favorites 时，管理需求与所属行业无关
 *
 * @param originLabelData 后端返回的原始标签数据
 */
export const isFavoritesStandalone = (originLabelData?: Record<string, any>) => {
    return !isEmpty(originLabelData?.favorites);
};

/**
 * 获取管理需求的选项列表，兼容两种数据来源
 *
 * 1. personaLabel.favorites 有值：「单独配置」场景，直接取该列表
 * 2. personaLabel.favorites 无值：「联动」场景，按行业取 industries[industry]
 *
 * @param originLabelData 后端返回的原始标签数据
 * @param industry 行业分类（仅联动场景使用）
 */
export const getFavoriteOptions = (originLabelData?: Record<string, any>, industry = '') => {
    // 单独配置取 favorites，联动场景按行业取 industries[industry]
    const favoriteKeys = isFavoritesStandalone(originLabelData)
        ? originLabelData?.favorites
        : get(originLabelData, `industries.${industry}`);
    const favorites = getFavoritesIntl();
    return map(favoriteKeys, (favorite) => {
        return { value: favorite, text: get(favorites, favorite, favorite) };
    });
};

/**
 * 获取用户画像中的角色（职位）
 *
 * @param originLabelData 后端返回的原始标签数据
 */
export const getJobs = (originLabelData: Record<string, any>) => {
    const jobs = getJobsIntl();
    return map(get(originLabelData, 'jobs'), (job) => {
        return { value: job, text: get(jobs, job) };
    });
};

/**
 * 获取用户画像中的【了解渠道】标签数据
 *
 * @param originLabelData 后端返回的原始标签数据
 */
export const getReferrers = (originLabelData: Record<string, any>) => {
    const referrers = getReferrersIntl();
    return map(get(originLabelData, 'referrers'), (referrer) => {
        return { value: referrer, text: get(referrers, referrer) };
    });
};

export type PersonaFieldKey = 'requirement' | 'industry' | 'favorites' | 'job' | 'referrer';

const PERSONA_FIELD_CHECKERS: Record<
    PersonaFieldKey,
    (
        originLabelData?: Record<string, any>,
        options?: {
            industry?: string;
        }
    ) => boolean
> = {
    requirement: (originLabelData) => !isEmpty(get(originLabelData, 'requirement')),
    industry: (originLabelData) => !isEmpty(keys(get(originLabelData, 'industries'))),
    favorites: (originLabelData, options = {}) => {
        // 后端直接下发了 favorites 列表：管理需求为【单独配置】，与所属行业无关
        if (isFavoritesStandalone(originLabelData)) {
            return true;
        }
        // 未下发 favorites：管理需求与所属行业【联动】，取当前行业下的应用场景
        const { industry = '' } = options;
        return !isEmpty(industry) && !isEmpty(get(originLabelData, `industries.${industry}`));
    },
    job: (originLabelData) => !isEmpty(get(originLabelData, 'jobs')),
    referrer: (originLabelData) => !isEmpty(get(originLabelData, 'referrers'))
};

/**
 * 判断用户画像中的某项标签字段是否可用
 */
export const isPersonaFieldAvailable = (
    originLabelData: Record<string, any> | undefined,
    field: PersonaFieldKey,
    options: {
        industry?: string;
    } = {}
) => {
    return PERSONA_FIELD_CHECKERS[field](originLabelData, options);
};

/**
 * 获取用户画像中所有字段的可用信息
 *
 * 完全按照后端返回的标签数据结构判断：
 * - 管理需求：label.favorites 有值即为【单独配置】；否则按 industries[industry] 【联动】
 *
 * @param originLabelData 后端返回的原始标签数据
 * @param industry 当前选中的行业（仅管理需求联动场景使用）
 */
export const getPersonaCollectAvailableFields = (
    originLabelData?: Record<string, any>,
    industry = ''
) => {
    return {
        requirement: isPersonaFieldAvailable(originLabelData, 'requirement'),
        industry: isPersonaFieldAvailable(originLabelData, 'industry'),
        favorites: isPersonaFieldAvailable(originLabelData, 'favorites', { industry }),
        job: isPersonaFieldAvailable(originLabelData, 'job'),
        referrer: isPersonaFieldAvailable(originLabelData, 'referrer')
    };
};

const PERSONA_PAYLOAD_FIELD_MAP = [
    ['job', 'job'],
    ['industry', 'industry'],
    ['favorites', 'favorites'],
    ['requirement', 'requirement'],
    ['referrer', 'referrer']
] as const;

type PersonaPayloadField = (typeof PERSONA_PAYLOAD_FIELD_MAP)[number][0];

/**
 * 按照可用字段生成用户画像数据
 */
export const getAvailablePersonaData = (
    availableFields: Record<PersonaPayloadField, boolean>,
    data: Partial<Record<PersonaPayloadField, any>>
) => {
    return PERSONA_PAYLOAD_FIELD_MAP.reduce<Record<string, any>>(
        (result, [field, payloadField]) => {
            if (availableFields[field]) {
                result[payloadField] = data[field];
            }
            return result;
        },
        {}
    );
};

/**
 * 是否展示邀请引导
 *
 * @param corpUserStore CorpUserModel
 * @returns {boolean} needInviteGuide
 */
export const needInviteGuide = (corpUserStore: CorpUserModel) => {
    const { isIntegratedCorpType, isCorpOwner, userId } = corpUserStore;

    if (isIntegratedCorpType || !isCorpOwner) {
        return false;
    }

    const inviteMemberKey = StorageKeyManager.getInviteMemberKey({
        userId: userId
    });
    const inviteStatus = localStorageManager.get(inviteMemberKey);

    // 兼容历史数据
    if (inviteStatus === 'true') {
        return false;
    }

    // 只有当状态为 'need' 时才显示邀请引导
    // undefined: 未初始化，不显示
    // 'need': 需要显示
    // 'done': 不需要显示
    return inviteStatus === MEMBER_INVITE_FLAG.NEED || inviteStatus === 'true';
};

export const getIntegratedPlatformName = (corpType?: CorpType) => {
    switch (corpType) {
        case CorpType.DingTalk:
            return Intl.get('dingtalk');
        case CorpType.WxWork:
            return Intl.get('wework');
        case CorpType.Lark:
            return Intl.get('lark');
        default:
            return '';
    }
};

/**
 * 设置成员引导的状态
 *
 * @param memberLength 成员数量
 * @param corpUserStore
 */
export const handleInviteCache = (memberLength: number, corpUserStore: CorpUserModel) => {
    const inviteMemberKey = StorageKeyManager.getInviteMemberKey({
        userId: corpUserStore.userId
    });

    if (memberLength > 1) {
        // 成员数大于1，设置为不需要显示邀请引导
        localStorageManager.set(inviteMemberKey, MEMBER_INVITE_FLAG.DONE, 365);
    } else {
        // 成员数小于等于1，设置为需要显示邀请引导
        localStorageManager.set(inviteMemberKey, MEMBER_INVITE_FLAG.NEED, 365);
    }
};

/**
 * 获取部门/互联组织名称
 *
 * @param corpInfo
 */
export const getDepartmentName = (corpInfo: any) => {
    let name = get(corpInfo, 'name');
    if (!name) {
        return '';
    }
    const { status, type } = corpInfo;
    if (status === CONTACTS.CORP_CONNECT_STATUS.INACTIVE && type === DeptType.corp_coop) {
        name += Intl.get('corp.removed.suffix');
    }
    return name;
};
